import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Megaphone } from "lucide-react";
import { inputCls, btnPrimary, formatErr } from "@/components/admin/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Toggle = ({ checked, onChange, testid, label }) => (
    <label className="flex items-center gap-3 cursor-pointer select-none">
        <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={testid}
            className="w-4 h-4 accent-amber-500"
        />
        <span className="font-meta text-[10px] tracking-[0.2em] uppercase text-zinc-400">{label}</span>
    </label>
);

export const AnnouncementPanel = ({ token }) => {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/site-settings`)
            .then((res) => setForm(res.data.announcement))
            .catch(() => setForm({ enabled: false, text: "", link_url: "", link_label: "" }));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await axios.put(`${API}/admin/announcement`, form, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setForm(data);
            toast.success(data.enabled ? "Announcement is live on the site." : "Announcement switched off.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not save the announcement."));
        } finally {
            setSaving(false);
        }
    };

    if (form === null) {
        return <div className="rounded-2xl border border-amber-500/10 bg-zinc-900/40 h-40 animate-pulse" />;
    }

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    return (
        <section data-testid="admin-announcement-tab" className="max-w-2xl">
            <div className="rounded-2xl border border-amber-500/25 bg-zinc-900/60 backdrop-blur-md p-5 sm:p-7">
                <div className="flex items-center gap-2 text-amber-300 mb-5">
                    <Megaphone size={16} />
                    <p className="font-meta text-[10px] tracking-[0.3em] uppercase">Top strip</p>
                </div>
                <Toggle
                    checked={form.enabled}
                    onChange={(enabled) => setForm((f) => ({ ...f, enabled }))}
                    testid="admin-announcement-enabled"
                    label="Show the announcement on every page"
                />
                <div className="grid gap-3 mt-5">
                    <input
                        value={form.text}
                        onChange={set("text")}
                        placeholder="One line — e.g. New album out now"
                        data-testid="admin-announcement-text"
                        className={inputCls}
                    />
                    <input
                        value={form.link_label}
                        onChange={set("link_label")}
                        placeholder="Button label (optional) — e.g. Listen"
                        data-testid="admin-announcement-link-label"
                        className={inputCls}
                    />
                    <input
                        value={form.link_url}
                        onChange={set("link_url")}
                        placeholder="Link (optional) — /music or https://…"
                        data-testid="admin-announcement-link-url"
                        className={inputCls}
                    />
                </div>
                <button onClick={save} disabled={saving} data-testid="admin-announcement-save" className={`${btnPrimary} mt-5`}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    {saving ? "Saving…" : "Save Announcement"}
                </button>
                <p className="text-xs text-zinc-500 mt-4">
                    Visitors can dismiss the strip for their session; it comes back on their next visit.
                </p>
            </div>
        </section>
    );
};

export const AlertsCard = ({ token }) => {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/admin/alerts`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setForm(res.data))
            .catch(() => setForm({ enabled: true, email: "", last_error: null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await axios.put(
                `${API}/admin/alerts`,
                { enabled: form.enabled, email: form.email },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setForm(data);
            toast.success("Email alert settings saved.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not save the alert settings."));
        } finally {
            setSaving(false);
        }
    };

    if (form === null) return null;

    return (
        <div data-testid="account-alerts-card" className="rounded-2xl border border-amber-500/25 bg-zinc-900/60 backdrop-blur-md p-5 sm:p-7">
            <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-amber-300">Email alerts</p>
            <h2 className="font-display text-lg font-bold text-white mt-2 mb-5">Inquiries & signups</h2>
            <Toggle
                checked={form.enabled}
                onChange={(enabled) => setForm((f) => ({ ...f, enabled }))}
                testid="account-alerts-enabled"
                label="Email me every inquiry and signup"
            />
            <input
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="you@example.com"
                data-testid="account-alerts-email"
                className={`${inputCls} mt-4`}
            />
            <button onClick={save} disabled={saving} data-testid="account-alerts-save" className={`${btnPrimary} mt-4`}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {saving ? "Saving…" : "Save Alerts"}
            </button>
            {form.last_error && (
                <p data-testid="account-alerts-last-error" className="text-xs text-red-300/90 mt-4 leading-relaxed">
                    Last send failed — {form.last_error}
                </p>
            )}
            <p className="text-xs text-zinc-500 mt-4">
                Submissions always save to your Inbox, even if an email fails. Replying to an inquiry alert answers the fan directly.
            </p>
        </div>
    );
};

export default AnnouncementPanel;
